import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentMethod } from '@src/types/constants'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledDebouncedNumberField,
  ControlledSelect,
  ControlledTextField,
} from '@if/mui'
import DeleteIcon from '@mui/icons-material/Delete'
import UndoIcon from '@mui/icons-material/Undo'
import { alpha, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import { useUser } from '@src/providers/UserProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { memo, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { JSX } from 'react/jsx-runtime'
import { editPaymentFieldIsDisabled, EditPaymentSchemaType, EditPaymentsSchemaType } from './EditPayments-schema'

interface EditPaymentTableRowProps {
  index: number
  onCriticalChange: (index: number, sortPayments?: boolean) => void
  onUncriticalChange: (index: number, sortPayments?: boolean) => void
  paymentMethodOptions: JSX.Element[]
  onDelete: (index: number, payment: EditPaymentSchemaType) => void
  onRestore: (index: number, payment: EditPaymentSchemaType) => void
  disabled?: boolean
  loanActivationDate: DateOnly
}

const EditPaymentTableRow = ({
  index,
  onCriticalChange,
  onUncriticalChange,
  paymentMethodOptions,
  onDelete,
  onRestore,
  disabled = false,
  loanActivationDate,
}: EditPaymentTableRowProps) => {
  const { control, setValue, trigger } = useFormContext<EditPaymentsSchemaType>()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false

  // Optimization: useWatch to subscribe only to the specific row data
  // This prevents the entire table from re-rendering on every change
  const payment = useWatch({
    name: `payments.${index}`,
    control,
  })
  const handleCriticalChangeWithSorting = useCallback(() => {
    onCriticalChange(index, true)
  }, [index, onCriticalChange])

  const handleCriticalChange = useCallback(() => {
    onCriticalChange(index)
  }, [index, onCriticalChange])

  const handleUncriticalChange = useCallback(() => {
    onUncriticalChange(index)
  }, [index, onUncriticalChange])

  const resetSendSmsReminderOnPaymentMethodChanged = useCallback(
    (value: EPaymentMethod | null) => {
      if (!value) {
        return
      }
      setValue(`payments.${index}.sendSmsReminder`, false, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
      void trigger(`payments.${index}.effectiveDate`)
      handleUncriticalChange()
    },
    [index, handleUncriticalChange, setValue, trigger],
  )

  const handleDeleteOrRestore = useCallback(() => {
    if (payment.isDeleted) onRestore(index, payment)
    else onDelete(index, payment)
  }, [index, onDelete, onRestore, payment])

  const fieldDisabled = useMemo<{
    [K in keyof Pick<
      EditPaymentSchemaType,
      'effectiveDate' | 'amount' | 'paymentMethod' | 'note' | 'sendSmsReminder'
    >]: boolean
  }>(
    () => ({
      effectiveDate: disabled || editPaymentFieldIsDisabled(payment, 'effectiveDate'),
      amount: disabled || editPaymentFieldIsDisabled(payment, 'amount'),
      paymentMethod: disabled || editPaymentFieldIsDisabled(payment, 'paymentMethod'),
      note: disabled || editPaymentFieldIsDisabled(payment, 'note'),
      sendSmsReminder: disabled || editPaymentFieldIsDisabled(payment, 'sendSmsReminder'),
    }),
    [payment, disabled],
  )

  const minStartDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance().getMinPaymentDate(
        payment.paymentMethod,
        loanActivationDate,
        canBypassAcpLock,
      ),
    [payment.paymentMethod, canBypassAcpLock, loanActivationDate],
  )

  return (
    <TableRow
      sx={(theme) => ({
        verticalAlign: 'top',
        backgroundColor: payment.isDeleted ? alpha(theme.palette.error.main, 0.12) : 'inherit',
      })}
    >
      <TableCell align="center" style={{ width: '1%' }}>
        <Typography variant="body2" fontWeight="normal" sx={{ mt: 1.5 }}>
          {payment.paymentNumber ?? ''}
        </Typography>
      </TableCell>

      <TableCell style={{ width: '1%' }}>
        <ControlledDateOnlyPicker
          name={`payments.${index}.effectiveDate`}
          control={control}
          sx={{ maxWidth: 175 }}
          slotProps={{ textField: { size: 'small' } }}
          onChange={handleCriticalChangeWithSorting}
          disabled={fieldDisabled.effectiveDate}
          minDate={minStartDate}
        />
      </TableCell>

      <TableCell style={{ width: '1%' }}>
        <ControlledDebouncedNumberField
          name={`payments.${index}.amount`}
          control={control}
          size="small"
          style={{ maxWidth: 120 }}
          onChange={handleCriticalChange}
          disabled={fieldDisabled.amount}
          debounceTime={500}
        />
      </TableCell>

      <TableCell style={{ width: '1%' }}>
        <ControlledSelect
          name={`payments.${index}.paymentMethod`}
          control={control}
          options={paymentMethodOptions}
          disableEmptyOption
          size="small"
          onChange={resetSendSmsReminderOnPaymentMethodChanged}
          disabled={fieldDisabled.paymentMethod}
        />
      </TableCell>

      <TableCell>
        <ControlledTextField
          name={`payments.${index}.note`}
          disabled={fieldDisabled.note}
          onChange={handleUncriticalChange}
          control={control}
          size="small"
          label={t('loan.payment.description')}
          placeholder={t('loan.payment.options.addNote')}
          fullWidth
        />
      </TableCell>

      <TableCell align="center" style={{ width: '1%' }}>
        <ControlledCheckbox
          name={`payments.${index}.sendSmsReminder`}
          control={control}
          style={{ margin: 0 }}
          onChange={handleUncriticalChange}
          disabled={fieldDisabled.sendSmsReminder}
        />
      </TableCell>

      <TableCell align="center" style={{ width: '1%' }}>
        <Tooltip title={payment.isDeleted ? t('loan.payment.restore') : t('loan.payment.delete')}>
          <span>
            <IconButton
              onClick={handleDeleteOrRestore}
              color={payment.isDeleted ? 'inherit' : 'error'}
              disabled={disabled || payment.isLocked}
            >
              {payment.isDeleted ? <UndoIcon /> : <DeleteIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

export default memo(EditPaymentTableRow)
